import { memo, useEffect } from 'react';

import { type LeadFormTrigger, useTrackOpenedPlansModal } from '@air/analytics';
import { Modal } from '@air/primitive-modal';
import { Spinner } from '@air/primitive-spinner';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { UpgradePlanModalContent } from '~/components/Modals/PlansModal/UpgradePlanModalContent';
import { usePlansList } from '~/hooks/usePlansList';
import { type EnhancedSubscription } from '~/providers/SubscriptionProvider';

export type UpgradePlanModalProps = {
  leadFormTrigger?: LeadFormTrigger;
  subscription: EnhancedSubscription;
};

export const UpgradePlanModal = memo(
  ({ leadFormTrigger, onClose, subscription }: AirModalProps<UpgradePlanModalProps>) => {
    const { data, error, isLoading: isPlansListLoading } = usePlansList();
    const { trackOpenedPlansModal } = useTrackOpenedPlansModal();
    const { showToast } = useToasts();

    useEffect(() => {
      if (error) {
        reportErrorToBugsnag({ error, context: 'Failed to fetch plans from PaidPlans modal' });
        showToast('Something went wrong. Please try again later.');
        onClose();
      }
    }, [error, onClose, showToast]);

    useEffect(() => {
      trackOpenedPlansModal();
    }, [trackOpenedPlansModal]);

    // Business/enterprise workspace users should see the demo form modal instead
    if (subscription.plan === 'business' || subscription.plan === 'enterprise') {
      return <DemoFormModal trigger={leadFormTrigger} onClose={onClose} />;
    }

    return (
      <Modal
        dangerouslyBypassFocusLock
        isOpen
        onDismiss={onClose}
        className="mt-0 w-full max-w-[1200px] p-0 lg:mt-8"
        overlayClassName="py-0 sm:py-8 sm:px-8 lg:px-0"
      >
        {isPlansListLoading ? (
          <div className="flex flex-col justify-center border-t border-t-grey-5 sm:flex-row">
            <div className="flex min-h-[556px] flex-1 items-center justify-center">
              <Spinner className="text-teal-9" />
            </div>
          </div>
        ) : (
          <UpgradePlanModalContent plans={data?.plans ?? []} subscription={subscription} onClose={onClose} />
        )}
      </Modal>
    );
  },
);

UpgradePlanModal.displayName = 'UpgradePlanModal';
